import {Component, computed, effect, inject, OnInit, signal, viewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {Fluid} from 'primeng/fluid';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {TranslocoDirective, TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {
  AcceptedAgbRequest,
  CreateCustomerUserRequest,
  CreateTenantAndUserRequestParams,
  CreateTenantRequest,
  CustomerRegisterControllerService,
  InvoiceDataRequest,
  RegisterTenantAndCustomerUserRequest,
  TenantType,
} from '@frontend/shared-api';
import {EMPTY, finalize, firstValueFrom} from 'rxjs';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormValidationService} from '../../../../shared/services/form-validation.service';
import {FormFieldComponent} from '../../../../shared/components/inputs/form-field/form-field.component';
import {Button} from 'primeng/button';
import {DirectusService, FooterLink, ModuleFunction, PaymentPlan} from '@frontend/directus';
import {Checkbox} from 'primeng/checkbox';
import {LinkTabOpenerDirective} from '../../../../shared/directives/link-tab-opener.directive';
import {APP_ROUTES, REGISTRATION_PLAN_QUERY_PARAM} from '../../../../app.routes';
import {Message} from 'primeng/message';
import {FunctionSelectionDialogComponent} from '../../../../shared/components/dialog/function-selection-dialog/function-selection-dialog.component';
import {FunctionDisplayService} from '../../../../shared/services/function-display.service';
import {PaymentPlanSelectComponent} from '../../../../shared/components/payment-plan-select/payment-plan-select.component';
import {TooltipModule} from 'primeng/tooltip';
import {BillingFormComponent} from '../../../../shared/components/billing/billing-form/billing-form.component';

@Component({
  selector: 'app-registration',
  imports: [
    CommonModule,
    Fluid,
    FormsModule,
    ReactiveFormsModule,
    FormFieldComponent,
    TranslocoPipe,
    Button,
    TranslocoDirective,
    Checkbox,
    LinkTabOpenerDirective,
    Message,
    FunctionSelectionDialogComponent,
    PaymentPlanSelectComponent,
    TooltipModule,
    BillingFormComponent,
  ],
  templateUrl: './registration.component.html',
})
export class RegistrationComponent implements OnInit {
  readonly functionsDialog = viewChild.required(FunctionSelectionDialogComponent);
  readonly billingForm = viewChild.required(BillingFormComponent);

  private readonly fb = inject(FormBuilder);
  private readonly registerService = inject(CustomerRegisterControllerService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly translocoService = inject(TranslocoService);
  private readonly formValidation = inject(FormValidationService);
  private readonly directusService = inject(DirectusService);
  private readonly functionDisplayService = inject(FunctionDisplayService);

  submitting = false;
  moduleFunctionDialogVisible = false;

  private readonly currentLanguageSignal = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  readonly form = this.fb.group(
    {
      selectedPlan: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      tenantName: ['', Validators.required],
      phoneNumber: ['', [Validators.required, FormValidationService.phoneValidator]],
      email: ['', [Validators.required, this.formValidation.emailValidator()]],
      password: [
        '',
        [Validators.required, Validators.minLength(8), this.formValidation.passwordValidator()],
      ],
      passwordRepeat: ['', Validators.required],
      acceptTerms: [false, Validators.requiredTrue],
    },
    {
      validators: this.formValidation.passwordMatchValidator(),
    },
  );

  private readonly selectedPlanControl = this.form.get('selectedPlan');
  private readonly selectedPlanId = toSignal(this.selectedPlanControl?.valueChanges ?? EMPTY, {
    initialValue: this.selectedPlanControl?.value ?? null,
  });

  termsOfUseLink: FooterLink | undefined;
  privacyPolicyLink: FooterLink | undefined;
  selectedModuleFunctions = signal<ModuleFunction[]>([]);
  allModuleFunctions = signal<ModuleFunction[]>([]);
  private readonly paymentPlansCache = signal<PaymentPlan[]>([]);

  protected readonly maxFunctions = computed(() => {
    const planId = this.selectedPlanId();
    if (!planId) return undefined;

    const selectedPlan = this.paymentPlansCache().find((plan) => plan.id === planId);
    return selectedPlan?.numberAvailableFunctions ?? undefined;
  });

  protected readonly allowCrossModule = computed(() => {
    const planId = this.selectedPlanId();
    if (!planId) return false;

    const selectedPlan = this.paymentPlansCache().find((plan) => plan.id === planId);
    return selectedPlan?.crossModuleFunctionsAllowed ?? false;
  });

  protected readonly isIndividualPlan = computed(() => {
    const planId = this.selectedPlanId();
    if (!planId) return false;

    const selectedPlan = this.paymentPlansCache().find((plan) => plan.id === planId);
    return selectedPlan?.isIndividualPlan ?? false;
  });

  constructor() {
    effect(() => {
      const maxFuncs = this.maxFunctions();
      const crossModule = this.allowCrossModule();
      const isIndividual = this.isIndividualPlan();
      const currentFunctions = this.selectedModuleFunctions();

      if (currentFunctions.length === 0) return;

      let shouldClear = false;

      if (isIndividual) {
        shouldClear = true;
      }

      if (maxFuncs !== undefined && currentFunctions.length > maxFuncs) {
        shouldClear = true;
      }

      if (!crossModule) {
        const moduleIds = new Set(currentFunctions.map((f) => f.module_id.id));
        if (moduleIds.size > 1) {
          shouldClear = true;
        }
      }

      if (shouldClear) {
        this.selectedModuleFunctions.set([]);
      }
    });
  }

  onPaymentPlansLoaded(plans: PaymentPlan[]): void {
    this.paymentPlansCache.set(plans);
    this.preselectRequestedPlan(plans);
  }

  private preselectRequestedPlan(plans: PaymentPlan[]): void {
    const requestedPlanId = this.route.snapshot.queryParamMap.get(REGISTRATION_PLAN_QUERY_PARAM);
    const isSelectablePlan = plans.some((plan) => plan.id === requestedPlanId);
    if (isSelectablePlan && !this.selectedPlanControl?.value) {
      this.selectedPlanControl?.setValue(requestedPlanId);
    }
  }

  protected readonly selectedFunctionsDisplay = computed(() => {
    const functions = this.selectedModuleFunctions();
    this.currentLanguageSignal();
    if (functions.length === 0) return [];

    const moduleFunctionMap = this.functionDisplayService.groupFunctionsByModule(functions);
    return this.functionDisplayService.displayAsModuleNameWithFunctionsCount(moduleFunctionMap);
  });

  protected readonly moduleFunctionDialogTranslationPrefix = 'account.register.moduleFunctions';

  ngOnInit(): void {
    this.loadFooterLinks();
    this.loadModuleFunctions();
  }

  private async loadModuleFunctions(): Promise<void> {
    const moduleFunctionsMap = await firstValueFrom(
      this.directusService.loadModuleFunctionsGroupedByModule(),
    );
    const allFunctions: ModuleFunction[] = [];
    for (const module of moduleFunctionsMap.values()) {
      if (module.functions) {
        allFunctions.push(...module.functions);
      }
    }
    this.allModuleFunctions.set(allFunctions);
  }

  private loadFooterLinks(): void {
    this.directusService.loadFooterLinks().subscribe({
      next: (response: FooterLink[]) => {
        this.termsOfUseLink = response.find(
          (link) => link.name === 'Terms of Use' || link.name === 'Terms and Conditions',
        );
        this.privacyPolicyLink = response.find((link) => link.name === 'Privacy Policy');
      },
    });
  }

  onSubmit() {
    const billingForm = this.billingForm();
    if (this.form.invalid || billingForm.form.invalid) {
      this.form.markAllAsTouched();
      billingForm.form.markAllAsTouched();
      return;
    }

    this.submitting = true;
    const registerRequest = this.buildCompleteRegistrationRequest();

    this.registerService
      .createTenantAndUser(registerRequest)
      .pipe(
        finalize(() => {
          this.submitting = false;
        }),
      )
      .subscribe({
        next: () => {
          this.router.navigate([APP_ROUTES.REGISTRATION_SUCCESS]);
        },
      });
  }

  private buildCompleteRegistrationRequest(): CreateTenantAndUserRequestParams {
    const formValues = this.form.value;

    const tenantRequest = this.buildTenantRequest(formValues);
    const userRequest = this.buildUserRequest(formValues);
    const invoiceDataRequest = this.billingForm().buildInvoiceDataRequest();
    const acceptedAgbRequest = this.buildAcceptedAgbRequest(formValues);
    const selectedPlanId = formValues.selectedPlan as string;

    const registerTenantAndUser = this.buildRegisterTenantAndUserRequest(
      tenantRequest,
      userRequest,
      invoiceDataRequest,
      acceptedAgbRequest,
      selectedPlanId,
    );

    return this.buildRegisterRequest(registerTenantAndUser);
  }

  private buildTenantRequest(
    formValues: Partial<{
      [K in keyof typeof this.form.value]: (typeof this.form.value)[K];
    }>,
  ): CreateTenantRequest {
    return {
      name: formValues.tenantName as string,
      tenantType: TenantType.CUSTOMER,
      acceptedAGB: formValues.acceptTerms ?? false,
    };
  }

  private buildUserRequest(
    formValues: Partial<{
      [K in keyof typeof this.form.value]: (typeof this.form.value)[K];
    }>,
  ): CreateCustomerUserRequest {
    return {
      firstName: formValues.firstName as string,
      lastName: formValues.lastName as string,
      phoneNumber: formValues.phoneNumber as string,
      email: formValues.email as string,
      password: formValues.password as string,
      languageCode: this.currentLanguageSignal() ?? 'en',
      tenant: formValues.tenantName as string,
    };
  }

  private buildAcceptedAgbRequest(
    formValues: Partial<{
      [K in keyof typeof this.form.value]: (typeof this.form.value)[K];
    }>,
  ): AcceptedAgbRequest {
    return {
      acceptedAGB: formValues.acceptTerms ?? false,
    };
  }

  private buildRegisterTenantAndUserRequest(
    tenantRequest: CreateTenantRequest,
    userRequest: CreateCustomerUserRequest,
    invoiceDataRequest: InvoiceDataRequest,
    acceptedAgbRequest: AcceptedAgbRequest,
    selectedPlanDirectusId: string,
  ): RegisterTenantAndCustomerUserRequest {
    const moduleFunctions = this.selectedModuleFunctions().map((func) => ({
      directusId: func.id,
    }));

    return {
      tenant: tenantRequest,
      user: userRequest,
      invoiceData: invoiceDataRequest,
      moduleFunctions,
      selectedPlan: {directusId: selectedPlanDirectusId},
      acceptedAgb: acceptedAgbRequest,
    };
  }

  private buildRegisterRequest(
    registerTenantAndCustomerUserRequest: RegisterTenantAndCustomerUserRequest,
  ): CreateTenantAndUserRequestParams {
    return {
      registerTenantAndCustomerUserRequest: registerTenantAndCustomerUserRequest,
    };
  }

  goBack() {
    this.router.navigate([APP_ROUTES.START]);
  }

  openModuleFunctionDialog(): void {
    this.moduleFunctionDialogVisible = true;
    const functionsDialog = this.functionsDialog();
    if (functionsDialog) {
      functionsDialog.initialSelectedFunctions.set([...this.selectedModuleFunctions()]);
      functionsDialog.resetAndInitialize();
    }
  }

  onModuleFunctionsSelected(functions: ModuleFunction[]): void {
    this.selectedModuleFunctions.set(functions);
    this.moduleFunctionDialogVisible = false;
  }
}
